import { ReactNode, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { OptroImportItem } from "../models/audit/optroSync";

interface OptroImportFullViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  items: OptroImportItem[];
  // The status cell for one item (icon, label, stage), owned by each card so
  // the controls and work-step wording stays with its card.
  renderStatus: (item: OptroImportItem) => ReactNode;
}

// The import card's "full view": every item of the import, searchable by
// name. It stays open until closed, even after the import finishes (as the
// document toast's full view does).
export const OptroImportFullViewDialog = ({
  open,
  onOpenChange,
  title,
  items,
  renderStatus,
}: OptroImportFullViewDialogProps) => {
  const [search, setSearch] = useState("");
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) =>
      item.controlName.toLowerCase().includes(term),
    );
  }, [items, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name"
            aria-label="Search imported items"
            className="pl-8"
          />
        </div>
        <ul className="max-h-[60vh] divide-y divide-stone-100 overflow-y-auto">
          {visible.length === 0 ? (
            <li className="py-6 text-center text-sm text-stone-400">
              No items match “{search}”.
            </li>
          ) : (
            visible.map((item) => (
              <li
                key={item.controlExternalRef}
                className="flex items-center gap-3 py-2 text-sm"
              >
                <span className="flex-1 truncate">{item.controlName}</span>
                {renderStatus(item)}
              </li>
            ))
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
